import { Button, Flex, Tabs, Text } from '@mantine/core';
import pluralize from 'pluralize';
import { useState } from 'react';
import { isRecordingFile } from 'src/data-capture/file-management/allowedFileTypes';
import { FileUploadInput, UploadedFile } from 'src/data-capture/hooks/file-uploads/types';
import { useFileUploadActions } from 'src/data-capture/hooks/file-uploads/useFileUploadActions';
import { useFileUploads } from 'src/data-capture/hooks/file-uploads/useFileUploads';
import SelectableItemList from 'src/UI/drawer-flow/SelectableItemList';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { StepCard } from 'src/UI/generate-flow/StepCard';
import {
  DOCUMENT_FILE_EXTENSIONS,
  DOCUMENT_MIME_TYPES,
} from 'src/meetings/detail/tabs/meeting-note/types';
import { MeetingNoteStepProps, MeetingNoteSourceType } from '../types';
import styles from './NoteSourceStep.module.css';

const PROCESSING_TOOLTIP =
  "This file is still being processed. You can generate a meeting note from it once it's ready.";

type SourceTab = 'recordings' | 'documents';

type SourceItem = {
  id: string;
  title: string;
  description: string;
  right?: string;
  disabled: boolean;
  disabledReason?: string;
};

function isFileProcessing(file: UploadedFile): boolean {
  const status = file.backgroundProcess?.status;
  return status === 'PENDING' || status === 'PROCESSING';
}

function fileToSelectableItem(file: UploadedFile): SourceItem {
  const processing = isFileProcessing(file);
  return {
    id: file.id,
    title: file.file.fileName,
    description: `Uploaded: ${new Date(file.file.createdAt).toLocaleDateString()}`,
    right: processing ? 'Processing' : undefined,
    disabled: processing,
    disabledReason: processing ? PROCESSING_TOOLTIP : undefined,
  };
}

type NoteSourcePanelProps = {
  itemNoun: string;
  items: SourceItem[];
  hint: string;
  sourceType: MeetingNoteSourceType;
  selectedRecordingId?: string;
  selectedSourceType?: MeetingNoteSourceType;
  onSelect: (selectedIds: string[], sourceType: MeetingNoteSourceType) => void;
  emptyView: React.ReactNode;
  headerAction?: React.ReactNode;
};

function NoteSourcePanel({
  itemNoun,
  items,
  hint,
  sourceType,
  selectedRecordingId,
  selectedSourceType,
  onSelect,
  emptyView,
  headerAction,
}: NoteSourcePanelProps) {
  const countText = (
    <Text size="textSm" fw={FONT_WEIGHTS.medium} c="black" style={{ flexShrink: 0 }}>
      {items.length} {pluralize(itemNoun, items.length)} found
    </Text>
  );

  return (
    <Flex direction="column" gap="xs" mih={0} flex={1} h="100%">
      {headerAction ? (
        <Flex justify="space-between" align="center" style={{ flexShrink: 0 }}>
          {countText}
          {headerAction}
        </Flex>
      ) : (
        countText
      )}
      <SelectableItemList
        items={items}
        hint={hint}
        selectedItemIds={
          selectedSourceType === sourceType && selectedRecordingId ? [selectedRecordingId] : []
        }
        setSelectedItemIds={selectedIds => onSelect(selectedIds, sourceType)}
        selectMultiple={false}
        emptyView={emptyView}
      />
    </Flex>
  );
}

export function NoteSourceStep({ data, setData }: MeetingNoteStepProps) {
  const clientId = data.isFamily ? data.family?.mainFamilyMember.clientId : data.client?.id;
  // For families, list the family's files (family-owned plus every member's files) so
  // recordings linked to any family member are selectable, not just the main member's.
  const familyId = data.isFamily ? data.family?.id : undefined;
  // Poll while any file is processing so a row that is still being ingested becomes
  // selectable as soon as its detection finishes, without the user reopening the flow.
  const { files, isLoading } = useFileUploads({ clientId, familyId, autoRefreshProcessing: true });
  const { uploadFiles } = useFileUploadActions({ clientId });
  const [isUploading, setIsUploading] = useState(false);

  const audioItems = filterAudioFiles(files).map(fileToSelectableItem);
  const documentItems = filterDocumentFiles(files).map(fileToSelectableItem);

  const defaultTab: SourceTab =
    audioItems.length === 0 && documentItems.length > 0 ? 'documents' : 'recordings';
  const [activeTab, setActiveTab] = useState<SourceTab>(defaultTab);

  const handleTabChange = (value: string | null) => {
    const tab = (value ?? 'recordings') as SourceTab;
    setActiveTab(tab);
    setData(prev => ({
      ...prev,
      selectedRecordingId: undefined,
      selectedSourceType: undefined,
      selectedSourceName: undefined,
    }));
  };

  const allItems = [...audioItems, ...documentItems];

  const handleSelection = (selectedIds: string[], sourceType: MeetingNoteSourceType) => {
    const selectedId = selectedIds[0] || undefined;
    const selectedItem = allItems.find(i => i.id === selectedId);
    setData(prev => ({
      ...prev,
      selectedRecordingId: selectedId,
      selectedSourceType: selectedId ? sourceType : undefined,
      selectedSourceName: selectedItem?.title,
    }));
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;

    if (fileList) {
      setIsUploading(true);
      try {
        const payload: FileUploadInput[] = Array.from(fileList).map(file => ({
          file,
          isResolved: true,
        }));
        const result = await uploadFiles(payload);
        const newUploadedFileId = result[0]?.data?.id;
        if (newUploadedFileId) {
          const uploadedFileName = Array.from(fileList)[0]?.name;
          setData(prev => ({
            ...prev,
            selectedRecordingId: newUploadedFileId,
            selectedSourceType: 'document',
            selectedSourceName: uploadedFileName,
          }));
        }
      } catch (error) {
        notifyAndLogError(
          'Failed to upload file',
          'An error occurred while uploading the document. Please try again.',
          error,
        );
      }
      setIsUploading(false);
    }

    event.target.value = '';
  };

  if (isLoading) return null;

  return (
    <Flex direction="column" h="100%" mih={0}>
      <StepCard title="Select source">
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          flex={1}
          mih={0}
          display="flex"
          style={{ flexDirection: 'column' }}
          classNames={{ list: styles.tabList, tab: styles.tab }}
        >
          <Tabs.List style={{ flexShrink: 0 }}>
            <Tabs.Tab value="recordings">Recordings ({audioItems.length})</Tabs.Tab>
            <Tabs.Tab value="documents">Documents ({documentItems.length})</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="recordings" pt="sm" flex={1} mih={0}>
            <NoteSourcePanel
              itemNoun="recording"
              items={audioItems}
              hint="The information already uploaded to the client's profile, financial and cashflows areas will be added as supplementary information in the meeting note."
              sourceType="audio"
              selectedRecordingId={data.selectedRecordingId}
              selectedSourceType={data.selectedSourceType}
              onSelect={handleSelection}
              emptyView={
                <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.10">
                  Upload a meeting recording (MP3, MP4, M4A, or WAV) to the client&apos;s documents
                  and it will appear here.
                </Text>
              }
            />
          </Tabs.Panel>

          <Tabs.Panel value="documents" pt="sm" flex={1} mih={0}>
            <NoteSourcePanel
              itemNoun="document"
              items={documentItems}
              hint="The document content will be used as the transcript for meeting note generation."
              sourceType="document"
              selectedRecordingId={data.selectedRecordingId}
              selectedSourceType={data.selectedSourceType}
              onSelect={handleSelection}
              emptyView={
                <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.10">
                  Upload a transcript document (PDF, Word, or text file) using the button above and
                  it will appear here.
                </Text>
              }
              headerAction={
                <Button
                  component="label"
                  variant="subtle"
                  size="compact-sm"
                  loading={isUploading}
                  style={{ cursor: 'pointer' }}
                >
                  Upload document
                  <input
                    type="file"
                    accept={DOCUMENT_FILE_EXTENSIONS.join(',')}
                    style={{ display: 'none' }}
                    onChange={handleFileUpload}
                  />
                </Button>
              }
            />
          </Tabs.Panel>
        </Tabs>
      </StepCard>
    </Flex>
  );
}

function filterAudioFiles(files: UploadedFile[]): UploadedFile[] {
  return files.filter(
    file => !file.isFolder && isRecordingFile(file.file.fileType, file.file.fileName),
  );
}

function filterDocumentFiles(files: UploadedFile[]): UploadedFile[] {
  return files.filter(file => DOCUMENT_MIME_TYPES.includes(file.file.fileType));
}
